import { ErrorBanner } from "@/components/board/ErrorBanner";
import { LoadingState } from "@/components/board/LoadingState";
import { Modal } from "@/components/Modal";
import { useToast } from "@/components/Toast";
import { useCategories, type Category } from "@/contexts/CategoriesContext";
import { useDealStages, type DealStage } from "@/contexts/DealStagesContext";
import { useRecentlyChangedRowsApi } from "@/contexts/RecentlyChangedRowsContext";
import { db, supabase } from "@/lib/supabase";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
  type Modifier,
} from "@dnd-kit/core";
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
  arrayMove,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { GripVertical, Pencil, Trash2 } from "lucide-react";
import { useCallback, useEffect, useState } from "react";

/** Keep the dragged row on the vertical axis. */
const restrictToVerticalAxis: Modifier = ({ transform }) => ({ ...transform, x: 0 });

/**
 * Clamp the dragged row within its parent (the tbody) so it can't be dragged
 * past the top or bottom of the list.
 */
const restrictToParentElement: Modifier = ({ containerNodeRect, draggingNodeRect, transform }) => {
  if (!draggingNodeRect || !containerNodeRect) return transform;
  const value = { ...transform };
  if (draggingNodeRect.top + transform.y <= containerNodeRect.top) {
    value.y = containerNodeRect.top - draggingNodeRect.top;
  } else if (
    draggingNodeRect.bottom + transform.y >=
    containerNodeRect.top + containerNodeRect.height
  ) {
    value.y = containerNodeRect.top + containerNodeRect.height - draggingNodeRect.bottom;
  }
  return value;
};

/** Counts how many projects reference a category. */
async function countCategoryUsage(categoryId: string): Promise<number> {
  const { count, error } = await supabase
    .from("projects")
    .select("id", { count: "exact", head: true })
    .eq("category_id", categoryId);
  // A Supabase query error resolves (it doesn't reject) with count: null, so we
  // surface it explicitly -- a failed count must not silently read as 0 and make
  // an in-use category look deletable.
  if (error) throw error;
  return count ?? 0;
}

export function CategoriesSettings() {
  const { categories, loading, error, refresh } = useCategories();
  const { dealStages, byId: dealStagesById } = useDealStages();
  const { addToast } = useToast();
  const { markSelfEdit } = useRecentlyChangedRowsApi();

  const [editing, setEditing] = useState<Category | null>(null);
  const [adding, setAdding] = useState(false);
  const [deleting, setDeleting] = useState<Category | null>(null);

  // Local ordered copy so drag reorders feel instant; reconciled from context.
  const [orderedCategories, setOrderedCategories] = useState<Category[]>(categories);
  useEffect(() => {
    setOrderedCategories(categories);
  }, [categories]);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const nextSortOrder =
    orderedCategories.length > 0 ? Math.max(...orderedCategories.map((c) => c.sort_order)) + 1 : 0;

  const handleDragEnd = useCallback(
    async (event: DragEndEvent) => {
      const { active, over } = event;
      if (!over || active.id === over.id) return;
      const oldIndex = orderedCategories.findIndex((c) => c.id === active.id);
      const newIndex = orderedCategories.findIndex((c) => c.id === over.id);
      if (oldIndex === -1 || newIndex === -1) return;

      const reordered = arrayMove(orderedCategories, oldIndex, newIndex);
      setOrderedCategories(reordered); // optimistic

      // Persist sort_order = index for every row whose position changed.
      const changed = reordered
        .map((c, index) => ({ c, index }))
        .filter(({ c, index }) => c.sort_order !== index);
      const results = await Promise.all(
        changed.map(({ c, index }) => {
          markSelfEdit("categories", c.id);
          return db.categories.update(c.id, { sort_order: index });
        }),
      );
      if (results.some((res) => res.error)) {
        addToast("Failed to save the new order.", "error");
        await refresh();
        return;
      }
      await refresh();
    },
    [orderedCategories, markSelfEdit, addToast, refresh],
  );

  return (
    <section className="settings-section">
      <div className="settings-section__header">
        <h2 className="settings-section__title">Categories</h2>
        <div className="settings-section__actions">
          <button type="button" className="primary-button" onClick={() => setAdding(true)}>
            Add Category
          </button>
        </div>
      </div>

      <p className="roles-settings__hint">
        Drag the handle on a row to change the order categories appear in. New projects in a category
        start at its default deal stage.
      </p>

      {error && <ErrorBanner message={error} entityName="categories" />}

      {loading ? (
        <LoadingState message="Loading categories…" />
      ) : (
        <div className="settings-table-wrapper">
          <table className="settings-table">
            <thead>
              <tr>
                <th className="settings-table__th settings-table__th--drag" aria-label="Reorder" />
                <th className="settings-table__th">Category</th>
                <th className="settings-table__th">Default Deal Stage</th>
                <th className="settings-table__th settings-table__th--access">Actions</th>
              </tr>
            </thead>
            <tbody>
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                modifiers={[restrictToVerticalAxis, restrictToParentElement]}
                onDragEnd={handleDragEnd}
              >
                <SortableContext
                  items={orderedCategories.map((c) => c.id)}
                  strategy={verticalListSortingStrategy}
                >
                  {orderedCategories.map((category) => (
                    <SortableCategoryRow
                      key={category.id}
                      category={category}
                      dealStageName={dealStagesById.get(category.default_deal_stage)?.name ?? "—"}
                      onEdit={() => setEditing(category)}
                      onDelete={() => setDeleting(category)}
                    />
                  ))}
                </SortableContext>
              </DndContext>
            </tbody>
          </table>
        </div>
      )}

      {(adding || editing) && (
        <CategoryEditModal
          category={editing}
          existing={categories}
          dealStages={dealStages}
          nextSortOrder={nextSortOrder}
          onClose={() => {
            setAdding(false);
            setEditing(null);
          }}
          onSaved={async () => {
            const wasEditing = !!editing;
            setAdding(false);
            setEditing(null);
            await refresh();
            addToast(wasEditing ? "Category updated." : "Category added.", "success");
          }}
        />
      )}

      {deleting && (
        <CategoryDeleteModal
          category={deleting}
          otherCategories={categories.filter((c) => c.id !== deleting.id)}
          onClose={() => setDeleting(null)}
          onDeleted={async () => {
            setDeleting(null);
            await refresh();
            addToast("Category deleted.", "success");
          }}
        />
      )}
    </section>
  );
}

interface SortableCategoryRowProps {
  category: Category;
  dealStageName: string;
  onEdit: () => void;
  onDelete: () => void;
}

/** A draggable category row. */
function SortableCategoryRow({ category, dealStageName, onEdit, onDelete }: SortableCategoryRowProps) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: category.id,
  });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.4 : undefined,
  };

  return (
    <tr ref={setNodeRef} style={style} className="settings-table__row" {...attributes}>
      <td className="settings-table__cell settings-table__cell--drag">
        <button
          type="button"
          className="roles-settings__drag-handle"
          {...listeners}
          tabIndex={-1}
          aria-label={`Drag ${category.name} to reorder`}
        >
          <GripVertical size={14} />
        </button>
      </td>
      <td className="settings-table__cell">{category.name}</td>
      <td className="settings-table__cell">{dealStageName}</td>
      <td className="settings-table__cell settings-table__cell--access">
        <div className="roles-settings__row-actions">
          <button
            type="button"
            className="icon-button"
            onClick={onEdit}
            aria-label={`Edit ${category.name}`}
          >
            <Pencil size={16} />
          </button>
          <button
            type="button"
            className="icon-button"
            onClick={onDelete}
            aria-label={`Delete ${category.name}`}
          >
            <Trash2 size={16} />
          </button>
        </div>
      </td>
    </tr>
  );
}

interface CategoryEditModalProps {
  category: Category | null; // null = add
  existing: Category[];
  dealStages: DealStage[];
  /** sort_order assigned to a newly-created category (appended to the end). */
  nextSortOrder: number;
  onClose: () => void;
  onSaved: () => void | Promise<void>;
}

function CategoryEditModal({
  category,
  existing,
  dealStages,
  nextSortOrder,
  onClose,
  onSaved,
}: CategoryEditModalProps) {
  const { markSelfEdit } = useRecentlyChangedRowsApi();
  const [name, setName] = useState(category?.name ?? "");
  const [defaultDealStage, setDefaultDealStage] = useState(
    category?.default_deal_stage ?? dealStages[0]?.id ?? "",
  );
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isValid = name.trim().length > 0 && defaultDealStage !== "";

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isValid || saving) return;

    const trimmed = name.trim();

    // Client-side distinctness check for a friendly error; the DB unique
    // constraint on name is the real backstop.
    const others = existing.filter((c) => c.id !== category?.id);
    if (others.some((c) => c.name.toLowerCase() === trimmed.toLowerCase())) {
      setError(`A category named "${trimmed}" already exists.`);
      return;
    }

    setSaving(true);
    setError(null);
    try {
      if (category) {
        // Sort order is changed by dragging in the list, not here.
        markSelfEdit("categories", category.id);
        const { error: updateError } = await db.categories.update(category.id, {
          name: trimmed,
          default_deal_stage: defaultDealStage,
        });
        if (updateError) {
          setError(friendlyCategoryError(updateError.message, trimmed));
          return;
        }
      } else {
        const id = crypto.randomUUID();
        const { error: insertError } = await db.categories.create({
          id,
          name: trimmed,
          sort_order: nextSortOrder,
          default_deal_stage: defaultDealStage,
        });
        if (insertError) {
          setError(friendlyCategoryError(insertError.message, trimmed));
          return;
        }
        markSelfEdit("categories", id);
      }
      await onSaved();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to save category");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal title={category ? "Edit Category" : "Add Category"} onClose={onClose} size="small">
      <form className="role-form" onSubmit={handleSubmit}>
        {error && (
          <div className="error-banner">
            <span className="error-banner__message">{error}</span>
          </div>
        )}

        <div className="form-field">
          <label className="form-label" htmlFor="category-name">
            Name <span className="required">*</span>
          </label>
          <input
            id="category-name"
            type="text"
            className="form-input"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. Upcoming"
            autoFocus
          />
        </div>

        <div className="form-field">
          <label className="form-label" htmlFor="category-default-deal-stage">
            Default Deal Stage <span className="required">*</span>
          </label>
          <select
            id="category-default-deal-stage"
            className="form-input"
            value={defaultDealStage}
            onChange={(e) => setDefaultDealStage(e.target.value)}
          >
            {dealStages.map((stage) => (
              <option key={stage.id} value={stage.id}>
                {stage.name}
              </option>
            ))}
          </select>
        </div>

        <div className="modal-actions">
          <button type="button" className="secondary-button" onClick={onClose} disabled={saving}>
            Cancel
          </button>
          <button type="submit" className="primary-button" disabled={!isValid || saving}>
            {saving ? "Saving…" : category ? "Save" : "Add Category"}
          </button>
        </div>
      </form>
    </Modal>
  );
}

interface CategoryDeleteModalProps {
  category: Category;
  otherCategories: Category[];
  onClose: () => void;
  onDeleted: () => void | Promise<void>;
}

function CategoryDeleteModal({ category, otherCategories, onClose, onDeleted }: CategoryDeleteModalProps) {
  const { markSelfEdit } = useRecentlyChangedRowsApi();
  const [usage, setUsage] = useState<number | null>(null);
  const [usageError, setUsageError] = useState(false);
  const [reassignTo, setReassignTo] = useState(otherCategories[0]?.id ?? "");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Load usage count on open.
  useEffect(() => {
    let cancelled = false;
    countCategoryUsage(category.id)
      .then((c) => {
        if (cancelled) return;
        setUsage(c);
        setUsageError(false);
      })
      // If we can't determine usage, block deletion rather than assuming the category
      // is unused -- deleting an in-use category would hit the projects FK.
      .catch(() => !cancelled && setUsageError(true));
    return () => {
      cancelled = true;
    };
  }, [category.id]);

  const inUse = usage !== null && usage > 0;

  const handleDelete = async () => {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      if (inUse) {
        if (!reassignTo) {
          setError("Choose a category to reassign to.");
          return;
        }
        const reassigned = await reassignCategory(category.id, reassignTo, markSelfEdit);
        if (!reassigned.ok) {
          setError(reassigned.message);
          return;
        }
      }
      markSelfEdit("categories", category.id);
      const { error: deleteError } = await db.categories.delete(category.id);
      if (deleteError) {
        setError(deleteError.message);
        return;
      }
      await onDeleted();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to delete category");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title={`Delete "${category.name}"`} onClose={onClose} size="small">
      <div className="role-form">
        {error && (
          <div className="error-banner">
            <span className="error-banner__message">{error}</span>
          </div>
        )}

        {usageError ? (
          <p>Couldn't check where this category is used. Try again before deleting.</p>
        ) : usage === null ? (
          <p>Checking where this category is used…</p>
        ) : inUse ? (
          <>
            <p>
              "{category.name}" is used by {usage} project{usage === 1 ? "" : "s"}. Reassign them to
              another category before deleting.
            </p>
            <div className="form-field">
              <label className="form-label" htmlFor="reassign-to">
                Reassign to <span className="required">*</span>
              </label>
              <select
                id="reassign-to"
                className="form-input"
                value={reassignTo}
                onChange={(e) => setReassignTo(e.target.value)}
              >
                {otherCategories.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </select>
            </div>
          </>
        ) : (
          <p>Are you sure you want to delete "{category.name}"?</p>
        )}

        <div className="modal-actions">
          <button type="button" className="secondary-button" onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <button
            type="button"
            className="danger-button"
            onClick={handleDelete}
            disabled={busy || usageError || usage === null || (inUse && !reassignTo)}
          >
            {busy ? "Deleting…" : inUse ? "Reassign & Delete" : "Delete Category"}
          </button>
        </div>
      </div>
    </Modal>
  );
}

/**
 * Reassigns every project using `fromId` to `toId` in a single bulk UPDATE
 * (one statement = one transaction), then the caller deletes the category.
 */
async function reassignCategory(
  fromId: string,
  toId: string,
  markSelfEdit: (table: string, id: string) => void,
): Promise<{ ok: true } | { ok: false; message: string }> {
  // Pre-mark the affected projects as self-edits so the realtime handler treats
  // the (about-to-arrive) row updates as ours, not external changes.
  const { data: affected, error: fetchError } = await supabase
    .from("projects")
    .select("id")
    .eq("category_id", fromId);
  if (fetchError) return { ok: false, message: fetchError.message };
  for (const p of affected ?? []) {
    markSelfEdit("projects", p.id);
  }

  const { error } = await supabase
    .from("projects")
    .update({ category_id: toId })
    .eq("category_id", fromId);
  if (error) return { ok: false, message: error.message };

  return { ok: true };
}

function friendlyCategoryError(message: string, name: string): string {
  if (/unique|duplicate key/i.test(message)) {
    return `A category named "${name}" already exists.`;
  }
  return message;
}
